import { ReactNode, useState } from 'react'
import styled, { css } from 'styled-components'
import { useHistory, useLocation } from 'react-router-dom'
import ConfettiExplosion from 'react-confetti-explosion'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { theseus } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { Checkbox } from '../../../../page_components/DropDetails/ChapterView'
import TaskBody from '../../TaskBody'
import { SubTask } from '../../../../page_components/PoolDetails/Missions/types'
import AnimatedLoader from '../../../AnimatedLoader'
import { ThemedButton } from '../../../ThemedButton'
import { Box } from '../../../UI/Box'
import { selectUser } from '../../../../modules/user/selectors'
import useIsLoggedIn from '../../../../hooks/useIsLoggedIn'
import ConnectWalletButton from '../../../Library/Buttons/ConnectWalletButton'
import DiscordConnectButton from '../../../SocialConnectButtons/Discord'

type DiscordInfo = {
  discordInviteLink: string
  discordServerId: string
}

export type Props = {
  subtask: SubTask
  taskId: number
  updateTaskSubmissions: () => Promise<unknown>
  taskAmount: ReactNode
}

const DiscordTask = ({
  subtask,
  taskId,
  updateTaskSubmissions,
  taskAmount,
}: Props) => {
  const { publicName, instructions } = subtask
  const title = publicName ?? 'Follow us on X'
  const description =
    instructions ?? 'Simply press the follow button below to get our updates'

  const [isLoadingVerify, setIsLoadingVerify] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const project = useSelector(selectCurrentProject)

  const location = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)

  const updateSearchParam = (paramName: string, value: string) => {
    searchParams.set(paramName, value)
    history.push(`${location.pathname}?${searchParams.toString()}`)
  }

  function selfDestruct(ms = 2000) {
    setTimeout(() => {
      updateSearchParam('active_mission', '')
    }, ms)
  }

  async function follow() {
    try {
      setError(null)
      setIsLoadingVerify(true)
      const { data } = await theseus.post(`/auth/discord/follow`, {
        subtaskId: subtask.id,
        projectId: project.id,
        taskId,
      })
      if (!data?.message)
        throw new Error("Bad response from server, couldn't follow")
      const { message } = data
      if (!message) throw new Error('Bad response')
      if (message !== 'success') throw new Error(message)
      await updateTaskSubmissions()
      selfDestruct()
      setIsComplete(true)
    } catch (error) {
      console.error(error?.message)
      setError(error.message)
    } finally {
      setIsLoadingVerify(false)
    }
  }

  const { discordInviteLink, discordServerId } = subtask

  const discordInfo = {
    discordInviteLink,
    discordServerId,
  }

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={description}
      error={
        error && error === 'No user to follow'
          ? "You've hammered the Discord API pretty hard! Love the effort but give it a few minutes to cool down!"
          : error
      }
      extra={taskAmount}
      actions={
        <ActionBlock
          discordInfo={discordInfo}
          follow={follow}
          isLoadingVerify={isLoadingVerify}
          isComplete={isComplete}
          hasError={!!error}
        />
      }
    />
  )
}

export default DiscordTask

const CheckContainer = styled(Box)<{ isVisible: boolean }>`
  max-width: 0px;
  overflow: hidden;
  opacity: 0;
  transition: 0.3s ease-in-out;
  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
      max-width: 28px;
      padding-right: 8px;
    `}
`

function ActionBlock({
  discordInfo,
  isLoading,
  isLoadingVerify,
  follow,
  isComplete,
  hasError,
}: {
  discordInfo: DiscordInfo | null
  isLoading?: boolean
  isLoadingVerify: boolean
  follow: () => void
  isComplete: boolean
  hasError: boolean
}) {
  const { isLoggedIn } = useIsLoggedIn()
  const user = useSelector(selectUser)
  const { discordHandle } = user ?? {}
  return (
    <>
      {!isLoggedIn ? (
        <ConnectWalletButton />
      ) : !discordHandle ? (
        <Box jc>
          <DiscordConnectButton label="Connect Discord"/>
        </Box>
      ) : (
        <Box ac jc g={16}>
          <ThemedButton
            onClick={() => {
              if (!discordInfo?.discordInviteLink) {
                toast.error('No discord invite link found')
                return
              }
              window.open(discordInfo?.discordInviteLink, '_blank')
            }}
            disabled={isLoading || isComplete}
            $styleType="yellow-transparent"
          >
            Join Server
          </ThemedButton>
          <ThemedButton
            onClick={follow}
            disabled={isLoading || isLoadingVerify || isComplete}
            $styleType="primary"
          >
            <Box ac jc>
              {isComplete && <ConfettiExplosion zIndex={10000} />}
              <CheckContainer isVisible={isComplete}>
                <Checkbox />
              </CheckContainer>
              {isLoadingVerify ? (
                <AnimatedLoader size={14} />
              ) : isComplete ? (
                'Verified'
              ) : (
                'Verify'
              )}
            </Box>
          </ThemedButton>
        </Box>
      )}
    </>
  )
}
